import { UserOptionLabel, ViperSelect } from "@/components";
import { ILimitedListUser } from "@/interfaces/Project/Roles/ILimitedListUser";
import { IRegionalProjectRole } from "@/interfaces/Project/Roles/IRegionalProjectRole";
import { useSuggestUsersQuery } from "@/services";
import { Button, Form, Modal, Select } from "antd";
import { useState } from "react";
import { useIntl } from "react-intl";

const { Option } = Select;

interface IProps {
  regionRole: IRegionalProjectRole;
  open: boolean;
  onClose(): void;
  onAddAnother(data: ILimitedListUser): void;
  onOk(data: ILimitedListUser): void;
}

export const AddLimitedListUserModal = ({
  regionRole,
  open,
  onClose,
  onAddAnother,
  onOk,
}: IProps) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [userQuery, setUserQuery] = useState<string>("");

  const { data: userSuggestions, isFetching: fetchingSuggestions } =
    useSuggestUsersQuery(
      { query: userQuery },
      {
        skip: !userQuery,
      }
    );

  const handleAdd = (addAnother: boolean): void => {
    form.validateFields().then(
      (data: { userId: string }) => {
        addAnother ? onAddAnother({ ...data }) : onOk({ ...data });
        form.resetFields();
      },
      () => {
        /* NO-OP, error message is handled in UI under dropdown */
      }
    );
  };

  const isDuplicateUser = (value: string): boolean =>
    regionRole.limitedListUsers.some((user) => user.userId === value);

  return (
    <Modal
      centered
      closable={false}
      title={intl.formatMessage({ id: "projectRoles.addLimitedListUser" })}
      open={open}
      footer={[
        <Button
          key="cancel"
          data-test-id="limited-list-modal-cancel"
          onClick={onClose}
        >
          {intl.formatMessage({ id: "general.cancel" })}
        </Button>,
        <Button
          key="addAnother"
          data-test-id="limited-list-modal-add-another"
          onClick={(): void => handleAdd(true)}
        >
          {intl.formatMessage({ id: "projectRoles.addAnotherUser" })}
        </Button>,
        <Button
          key="add"
          type="primary"
          data-test-id="limited-list-modal-add"
          onClick={(): void => handleAdd(false)}
        >
          {intl.formatMessage({ id: "general.add" })}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="userId"
          label={intl.formatMessage({ id: "projectRoles.userName" })}
          rules={[
            { required: true, message: "" },
            {
              // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
              validator(_, value) {
                if (value && isDuplicateUser(value)) {
                  return Promise.reject(
                    new Error(
                      intl.formatMessage({
                        id: "projectRoles.duplicateUserError",
                      })
                    )
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <ViperSelect
            optionLabelProp="label"
            customOptions={userSuggestions?.map(
              ({
                document: { id, displayName, department, email, jobTitle },
              }) => (
                <Option key={id} label={displayName}>
                  <UserOptionLabel
                    displayName={displayName}
                    department={department}
                    email={email}
                    jobTitle={jobTitle}
                  />
                </Option>
              )
            )}
            loading={fetchingSuggestions}
            onFetchSuggestions={(searchTerm): void => {
              setUserQuery(searchTerm);
            }}
            data-test-id="add-users-dialog-users-add"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
